import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { ToolAudience } from '../../constants/tool-audience';
import { BaseEntity } from './base.entity';
import { ConversationAgentRoleDefaultEntity } from './conversation-agent-role-default.entity';
import { ConversationAgentToolEntity } from './conversation-agent-tool.entity';
import { ConversationAnalyzerEntity } from './conversation-analyzer.entity';
import { ConversationVoiceProfilesEntity } from './conversation-voice-profiles.entity';

/**
 * Shape of `ConversationAgentEntity.metadata.publicAccess` — configuration for
 * the public, embeddable widget key. Stored inside the agent's `metadata` JSON
 * rather than as dedicated columns so the public-access policy can grow without
 * schema churn.
 */
export interface ConversationAgentPublicAccessModel {
	/**
	 * Origins permitted to use the agent's public key. REQUIRED for the key to
	 * work — an empty or missing list fails closed (the key is inert). Supports
	 * exact origins and `*` wildcards (e.g. `https://*.example.com`).
	 */
	allowedOrigins?: Array<string>;
}

@Entity('_conversation_agent')
export class ConversationAgentEntity extends BaseEntity {
	/**
	 * When set, this agent is a system agent whose runtime behavior (prompt, tools, LLM)
	 * comes from the code-based SystemAgentDefinition, not the database record.
	 * The DB record exists for FK integrity only. Only the Agent Architect is a
	 * system agent; a row still holding 'sales-helper' predates PD-16920 and is
	 * converted to a template agent (ConvertSalesHelperToTemplateAgent, and
	 * lazily by AgentTemplateService).
	 */
	@Column({
		name: 'systemAgentType',
		type: 'varchar',
		length: 100,
		nullable: true,
		unique: true,
	})
	@Index('idx_conversation_agent_system_type')
	public systemAgentType?: string | null;

	/**
	 * PD-16920 — the Workd agent template this agent was made from
	 * (`AGENT_TEMPLATES` in libs/shared constants/agent-templates.ts), or null
	 * for an agent built from scratch. Not unique: a tenant may make several
	 * agents from one template.
	 */
	@Column({
		name: 'templateKey',
		type: 'varchar',
		length: 100,
		nullable: true,
	})
	@Index('idx_conversation_agent_template_key')
	public templateKey?: string | null;

	/**
	 * PD-16920 — the template version whose content this agent holds. An
	 * agent that is not customized is moved to each new version (its
	 * template-owned fields rewritten); a customized one keeps its version and
	 * is shown the newer versions' change log. 0 marks an agent converted from
	 * the old code-defined Sales Helper with content of its own, so the notice
	 * covers every template version.
	 */
	@Column({
		name: 'templateVersion',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public templateVersion?: number | null;

	/**
	 * PD-16920 — true once a template-owned field (prompt, first message,
	 * description, audience, toolsets, caller authentication) was changed
	 * through the agent editor, after which template updates no longer
	 * overwrite the agent. Boolean (not raw tinyint) so reads are real
	 * booleans.
	 */
	@Column({
		name: 'templateCustomized',
		type: 'boolean',
		default: 0,
	})
	public templateCustomized!: boolean;

	/**
	 * PD-16920 — who the agent is for: 'internal' (signed-in staff),
	 * 'customer' or 'public' (see `ToolAudience`). Null on agents that have
	 * not declared one yet.
	 */
	@Column({
		name: 'audience',
		type: 'varchar',
		length: 20,
		nullable: true,
	})
	public audience?: ToolAudience | null;

	/**
	 * Public, discoverable key part for embedding this agent in an external
	 * website widget. When non-null, the agent is reachable via a public key of
	 * the form `wkda_<tenantAlias>_<publicApiKey>` — this column stores only the
	 * random part; the alias lives in the key string so the tenant DB can be
	 * resolved before context is set. It is an identifier, NOT a secret (it ships
	 * in page source); origin allow-listing in `metadata.publicAccess` is the
	 * defense. Null = the agent is not publicly embeddable.
	 */
	@Column({
		name: 'publicApiKey',
		type: 'varchar',
		length: 64,
		nullable: true,
		unique: true,
	})
	@Index('idx_conversation_agent_public_api_key')
	public publicApiKey?: string | null;

	@Column({ name: 'name', type: 'varchar', length: 100, nullable: false })
	public name!: string;

	@Column({ name: 'agent_type', type: 'varchar', length: 50, nullable: false })
	public agentType!: string;

	@Column({ name: 'fa_icon', type: 'varchar', length: 50, nullable: true })
	public faIcon?: string;

	@Column({ name: 'agent_color', type: 'varchar', length: 7, nullable: true })
	public agentColor?: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string;

	@Column({
		name: '_fk_conversation_voice_profile__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	// Null means the agent has no voice of its own and uses the tenant's
	// default voice (PD-12682).
	public voiceProfileId?: number | null;

	@Column({ name: 'first_message', type: 'text', nullable: false })
	public firstMessage!: string;

	@Column({ name: 'prompt', type: 'text', nullable: false })
	public prompt!: string;

	@Column({ name: 'is_active', type: 'boolean', width: 1, default: 1 })
	public isActive!: boolean;

	/**
	 * When true, the AI voice path's TwiML response includes a
	 * REST recording initiation at the media-stream start event
	 * (PD-14677 — inbound; outbound records via calls.create params).
	 * The recording-status webhook then persists the recording sid +
	 * URL onto `_conversation_call.metadata.recording`, and the inbox
	 * bubble surfaces a play button that streams via the AI recording
	 * proxy endpoint (parity with softphone calls).
	 *
	 * Defaults to true: recording was unconditional before this column
	 * existed, so the column-add must not silently disable it for any
	 * agent. Tenants/admins can flip it off per-agent for sensitive
	 * use cases.
	 */
	// type 'boolean' (not raw 'tinyint') so the mysql driver transforms
	// reads into real booleans — a raw tinyint column returns the NUMBER
	// 1/0, which silently fails strict `=== true` checks (the post-call
	// email's recording-wait gate, PD-14676; same family as PD-14797).
	// Identical tinyint(1) DDL either way.
	@Column({
		name: 'record_calls',
		type: 'boolean',
		default: 1,
	})
	public recordCalls!: boolean;

	/**
	 * Legacy engine columns, written with the platform's values when an agent
	 * is created and never read (PD-16872). PD-16964 keeps them unread: some
	 * rows still hold a value from before PD-16872, so the per-agent choice
	 * lives in the four columns below instead.
	 */
	@Column({
		name: 'llm_provider',
		type: 'varchar',
		length: 20,
		default: "'claude'",
	})
	public llmProvider!: string;

	@Column({ name: 'llm_model', type: 'varchar', length: 100, nullable: true })
	public llmModel?: string;

	/**
	 * PD-16964 — the AI model this agent's chat turns run on, set by Workd
	 * associates only (provider and model together; see `checkAgentLlmChoice`
	 * in constants/agent-llm.ts). Null means the platform default
	 * (`PLATFORM_LLM`). Voice turns use it too when no voice model is set.
	 */
	@Column({
		name: 'chatLlmProvider',
		type: 'varchar',
		length: 20,
		nullable: true,
		comment: 'AI model provider for chat turns (openai, claude); null = the platform default',
	})
	public chatLlmProvider?: string | null;

	@Column({
		name: 'chatLlmModel',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'AI model for chat turns; null = the platform default',
	})
	public chatLlmModel?: string | null;

	/**
	 * PD-16964 — the AI model this agent's voice turns run on, set by Workd
	 * associates only. Null means the chat model, else the platform default.
	 */
	@Column({
		name: 'voiceLlmProvider',
		type: 'varchar',
		length: 20,
		nullable: true,
		comment: 'AI model provider for voice turns (openai, claude); null = the chat model, else the platform default',
	})
	public voiceLlmProvider?: string | null;

	@Column({
		name: 'voiceLlmModel',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'AI model for voice turns; null = the chat model, else the platform default',
	})
	public voiceLlmModel?: string | null;

	@Column({
		name: 'voice_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'Voice provider used (elevenlabs, openai, etc.)',
	})
	public voiceProvider?: string;

	@Column({
		name: 'voice_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific voice model used (eleven_turbo_v2_5, tts-1, etc.)',
	})
	public voiceModel?: string;

	@Column({
		name: 'stt_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'STT provider used (deepgram, etc.)',
	})
	public sttProvider?: string;

	@Column({
		name: 'stt_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific STT model used (nova-2, nova-3-general, etc.)',
	})
	public sttModel?: string;

	@Column({
		name: 'stt_language',
		type: 'varchar',
		length: 20,
		nullable: true,
		default: "'en'",
		comment: 'Language code for STT (en, es, multi, etc.)',
	})
	public sttLanguage?: string;

	/**
	 * PD-16872 — the agent's speaking direction ("casual", "calm", or short
	 * custom wording), sent to the voice as a tone tag. Null means the voice
	 * profile's default direction applies.
	 */
	@Column({
		name: 'speakingDirection',
		type: 'varchar',
		length: 80,
		nullable: true,
		comment: 'Tone direction for the voice (casual, calm, ...); null = the voice default',
	})
	public speakingDirection?: string | null;

	/**
	 * PD-12682 — voice profile ids by spoken language code ({ "zh": 12 }),
	 * overriding the platform's voice for that language. The agent switches to
	 * the voice as soon as the listener knows the caller's language.
	 */
	@Column({
		name: 'languageVoices',
		type: 'json',
		nullable: true,
		comment: 'Voice profile id per spoken language code, overriding the platform defaults',
	})
	public languageVoices?: Record<string, number> | null;

	/**
	 * PD-17156 — the language a conversation starts in when the person has no
	 * language preference of their own (a phone caller, for one). Null means
	 * English. A signed-in user's own preference still comes first.
	 */
	@Column({
		name: 'preferredLanguage',
		type: 'varchar',
		length: 16,
		nullable: true,
		comment: 'Starting language when the person has no preference; null = English',
	})
	public preferredLanguage?: string | null;

	/**
	 * PD-17156 — the only languages the agent changes to, however the
	 * caller's words are heard. Null or empty means every language the
	 * platform speaks.
	 */
	@Column({
		name: 'spokenLanguages',
		type: 'json',
		nullable: true,
		comment: 'Language codes the agent may speak; null or empty = any',
	})
	public spokenLanguages?: Array<string> | null;

	@Column({
		name: 'stt_settings',
		type: 'json',
		nullable: true,
		comment: 'Provider-specific STT settings (e.g., Flux eager thresholds)',
	})
	public sttSettings?: Record<string, unknown>;

	@Column({
		name: 'fk__conversation_analyzer__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'Performance analyzer linked to this agent',
	})
	public analyzerId?: number;

	@ManyToOne(() => ConversationAnalyzerEntity, { nullable: true })
	@JoinColumn({ name: 'fk__conversation_analyzer__id' })
	public analyzer?: ConversationAnalyzerEntity;

	@Column({
		name: 'conversation_count',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public conversationCount!: number;

	@Column({ name: 'last_used', type: 'timestamp', nullable: true })
	public lastUsed?: Date;

	@Column({ name: 'metadata', type: 'json', nullable: true })
	public metadata?: any;

	@Column({
		name: 'monthlyMinuteLimit',
		type: 'decimal',
		precision: 10,
		scale: 2,
		nullable: true,
		default: null,
		comment:
			'Monthly voice minute limit for this agent (soft limit, nullable = no limit)',
	})
	public monthlyMinuteLimit?: number | null;

	// Relationships
	@ManyToOne(() => ConversationVoiceProfilesEntity, { nullable: true })
	@JoinColumn({ name: '_fk_conversation_voice_profile__id' })
	public voiceProfile?: ConversationVoiceProfilesEntity;

	@OneToMany(() => ConversationAgentToolEntity, (agentTool) => agentTool.agent)
	public agentTools?: ConversationAgentToolEntity[];

	@Column({
		name: 'is_chat_widget_default',
		type: 'boolean',
		width: 1,
		default: 0,
		comment: 'Instance-wide default chat widget agent',
	})
	public isChatWidgetDefault!: boolean;

	@OneToMany(
		() => ConversationAgentRoleDefaultEntity,
		(roleDefault) => roleDefault.agent,
	)
	public roleDefaults?: Array<ConversationAgentRoleDefaultEntity>;

	// Indexes
	@Index('idx_agent_type')
	public static agentTypeIndex: string = 'agent_type';

	@Index('idx_voice_profile')
	public static voiceProfileIndex: string =
		'_fk_conversation_voice_profile__id';

	@Index('idx_owner')
	public static ownerIndex: string = '_owner_userId';

	@Index('idx_active_owner', ['is_active', '_owner_userId'])
	public static activeOwnerIndex: [string, string] = [
		'is_active',
		'_owner_userId',
	];
}
